import { useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Pagination } from "@/components/pagination"
import { AdminHeader } from "@/components/admin-header"
import { Search, Ban, CheckCircle, ExternalLink, Loader2 } from "lucide-react"
import type AdminUser from "@/types/generated/AdminUser"
import type { Pagination as PaginationType } from "@/types"

type Props = {
  users: AdminUser[]
  pagination: PaginationType
  query: string | null
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

export default function Index({ users, pagination, query }: Props) {
  const [search, setSearch] = useState(query || "")
  const [banTarget, setBanTarget] = useState<AdminUser | null>(null)
  const [unbanTarget, setUnbanTarget] = useState<AdminUser | null>(null)
  const [processing, setProcessing] = useState(false)

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    router.get("/admin/users", search ? { q: search } : {}, { preserveState: true })
  }

  const handleBan = () => {
    if (!banTarget) return
    setProcessing(true)
    router.post(`/admin/users/${banTarget.id}/ban`, {}, {
      onSuccess: () => setBanTarget(null),
      onFinish: () => setProcessing(false),
    })
  }

  const handleUnban = () => {
    if (!unbanTarget) return
    setProcessing(true)
    router.post(`/admin/users/${unbanTarget.id}/unban`, {}, {
      onSuccess: () => setUnbanTarget(null),
      onFinish: () => setProcessing(false),
    })
  }

  return (
    <div className="min-h-screen">
      <Head title="Admin - Users" />
      <AdminHeader />

      <main className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-mono font-bold">USERS</h1>
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search users..."
                className="pl-9 w-64 font-mono"
              />
            </div>
            <Button type="submit" variant="secondary">Search</Button>
          </form>
        </div>

        <Card>
          <div className="divide-y divide-border">
            {users.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                No users found
              </div>
            ) : (
              users.map((user) => (
                <div key={user.id} className="p-4 flex items-center gap-4">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={user.avatar_url ?? undefined} />
                    <AvatarFallback>{user.name[0]}</AvatarFallback>
                  </Avatar>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{user.display_name || "Anonymous"}</span>
                      {user.is_admin && (
                        <Badge className="text-xs">Admin</Badge>
                      )}
                      {user.banned_at && (
                        <Badge variant="destructive" className="text-xs">Banned</Badge>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground flex items-center gap-3 mt-1">
                      <span title="OAuth name">{user.name}</span>
                      <span>{user.email || "No email"}</span>
                      <span>{user.samples_count} samples</span>
                      <span>Joined {formatDate(user.created_at)}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" asChild>
                      <Link href={`/users/${user.id}`}>
                        <ExternalLink className="h-4 w-4" />
                      </Link>
                    </Button>
                    {user.banned_at ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-green-500 hover:text-green-500"
                        onClick={() => setUnbanTarget(user)}
                      >
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setBanTarget(user)}
                        disabled={user.is_admin}
                      >
                        <Ban className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        <Pagination pagination={pagination} baseUrl="/admin/users" />
      </main>

      <AlertDialog open={!!banTarget} onOpenChange={(open) => !processing && !open && setBanTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ban User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to ban "{banTarget?.name}"? They will no longer be able to log in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBan} disabled={processing} className="bg-destructive hover:bg-destructive/90">
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Ban User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!unbanTarget} onOpenChange={(open) => !processing && !open && setUnbanTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unban User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unban "{unbanTarget?.name}"? They will be able to log in again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnban} disabled={processing}>
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Unban User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
